import React, { useEffect, useState, useCallback } from "react";
import { Plug, ShieldCheck, RefreshCw, LinkIcon, Loader2, CheckCircle2, AlertTriangle, Unplug, LogIn, Pause, Play, ChevronDown, PartyPopper, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useEntitlements } from "../context/EntitlementsContext";
import { fmtLocal, toDate } from "../utils/time";
import { TelegramButton } from "./TelegramButton";

const CONFETTI = Array.from({ length: 44 }, (_, i) => i);
const CONFETTI_COLORS = ["#2563eb", "#38bdf8", "#34d399", "#e6c25a", "#a78bfa", "#f472b6"];

const BrokerCelebration = ({ onEnter }) => (
  <div className="ws-celebrate" data-testid="broker-celebration">
    <div className="ws-confetti" aria-hidden>
      {CONFETTI.map((i) => (
        <span key={i} className="ws-confetti-piece" style={{
          left: `${(i * 2.27) % 100}%`,
          background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
          animationDelay: `${(i % 10) * 0.12}s`,
          animationDuration: `${2.4 + (i % 5) * 0.35}s`,
        }} />
      ))}
    </div>
    <div className="ws-celebrate-card ws-fade-up">
      <div className="ws-celebrate-icon"><PartyPopper className="w-9 h-9" /></div>
      <h2 className="ws-heading text-3xl font-extrabold mt-4">You're all in! 🎉</h2>
      <p className="text-[color:var(--ws-muted)] text-sm mt-2 max-w-sm mx-auto">
        Your broker is connected and <span className="ws-title-gradient font-semibold">full Pro access is unlocked</span> — every feature, on us. Your trades are syncing now.
      </p>
      <button className="ws-btn-primary ws-cta ws-cta-elefin mt-6 mx-auto justify-center px-6 py-3" onClick={onEnter} data-testid="celebration-enter">
        <span className="ws-cta-shine" aria-hidden />
        Enter the platform <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Safe response reader: never call res.json() blindly. If the proxy returns an
// HTML error/timeout page (<!DOCTYPE ...), surface a clear message instead of a
// "Unexpected token '<'" crash.
async function readJson(res) {
  const ctype = res.headers.get("content-type") || "";
  if (ctype.includes("application/json")) {
    try {
      return await res.json();
    } catch {
      /* fall through */
    }
  }
  const text = await res.text().catch(() => "");
  if (text.trim().startsWith("<")) {
    throw new Error(
      res.status === 504 || res.status === 502
        ? "MT5 is taking longer than usual to respond. Please click Sync Now in a moment."
        : `Unexpected server response (${res.status}). Please try again.`
    );
  }
  throw new Error(text || `Request failed (${res.status}).`);
}

const STATUS_MAP = {
  connected: { label: "Connected", cls: "ws-up", Icon: CheckCircle2 },
  syncing: { label: "Syncing", cls: "text-[color:var(--ws-blue-bright)]", Icon: Loader2 },
  synced: { label: "Synced", cls: "ws-up", Icon: CheckCircle2 },
  connection_error: { label: "Connection Error", cls: "ws-down", Icon: AlertTriangle },
  disconnected: { label: "Disconnected", cls: "text-[color:var(--ws-muted)]", Icon: Unplug },
};

function relativeTime(iso) {
  if (!iso) return "never";
  const d = toDate(iso);
  if (!d) return "never";
  const then = d.getTime();
  const s = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (s < 10) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const REFRESH_INTERVAL_MS = 10000; // read latest persisted state from our DB every 10s

// Extracts a human message from either a structured {detail:{message,stage}}
// error or a plain {detail:"..."} error, without ever exposing secrets.
function errText(data, fallback) {
  const d = data && data.detail;
  if (d && typeof d === "object") return d.message || fallback;
  if (typeof d === "string") return d;
  return fallback;
}

const RANGES = [
  { label: "30D", days: 30 },
  { label: "90D", days: 90 },
  { label: "1Y", days: 365 },
  { label: "All", days: 0 },
];

const BROKERS = {
  Elefin: ["Elefin-Trade"],
  XM: [
    "XMGlobal-MT5", "XMGlobal-MT5 2", "XMGlobal-MT5 4", "XMGlobal-MT5 5",
    "XMGlobal-MT5 6", "XMGlobal-MT5 7", "XMGlobal-MT5 8", "XMGlobal-MT5 9",
    "XMGlobal-MT5 10", "XMGlobal-MT5 11", "XMGlobal-MT5 12", "XMGlobal-MT5 13",
    "XMGlobal-MT5 14", "XMGlobal-MT5 15", "XMGlobal-MT5 16", "XMGlobal-MT5 17",
    "XMGlobal-MT5 18", "XMGlobal-MT5 19", "XMGlobal-MT5 20",
  ],
};

const ConnectMT5 = ({ onSynced, rangeDays = 0, setRangeDays }) => {
  const { user, login } = useAuth();
  const { refresh: refreshEnt } = useEntitlements();
  const navigate = useNavigate();
  const [celebrate, setCelebrate] = useState(false);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(() => {
    let broker = "", server = "";
    try {
      const pref = localStorage.getItem("ws_pref_broker");
      if (pref && BROKERS[pref]) { broker = pref; if (BROKERS[pref].length === 1) server = BROKERS[pref][0]; localStorage.removeItem("ws_pref_broker"); }
    } catch { /* ignore */ }
    return { login: "", password: "", server, broker };
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [autoOn, setAutoOn] = useState(true);
  const [tick, setTick] = useState(0); // forces "x ago" label to refresh

  const loadAccount = useCallback(async (alsoRefreshData = false) => {
    if (!user) { setLoading(false); return; }
    try {
      const res = await fetch(`${API}/mt5/account`, { credentials: "include" });
      if (res.ok) {
        const data = await readJson(res);
        setAccount(data.account);
      }
    } catch {}
    setLoading(false);
    if (alsoRefreshData && onSynced) onSynced();
  }, [user, onSynced]);

  useEffect(() => { loadAccount(); }, [loadAccount]);

  const isConnected = !!(account && account.connection_status && account.connection_status !== "disconnected");

  // Explicit incremental sync (used by the "Sync Now" button). Day-to-day freshness
  // is handled by the managed backend scheduler, so the browser does NOT poll the
  // Worker on a timer — it only reads our persisted state.
  const triggerSync = useCallback(async () => {
    try {
      await fetch(`${API}/mt5/sync`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({}),
      });
      setTimeout(() => loadAccount(true), 3000);
      setTimeout(() => loadAccount(true), 8000);
    } catch {}
  }, [loadAccount]);

  // Lightweight polling of PERSISTED state only (cheap DB reads, no Worker calls).
  useEffect(() => {
    if (!user || !isConnected || !autoOn) return;
    loadAccount(true);
    const refreshTimer = setInterval(() => loadAccount(true), REFRESH_INTERVAL_MS);
    const labelTimer = setInterval(() => setTick((t) => t + 1), 15000);
    const onVis = () => { if (document.visibilityState === "visible") loadAccount(true); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(refreshTimer);
      clearInterval(labelTimer);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [user, isConnected, autoOn, loadAccount]);

  const connect = async () => {
    setMsg(null);
    if (!form.login || !form.password || !form.server) {
      setMsg({ type: "err", text: "Account ID, investor password and server are required." });
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/mt5/connect`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      const data = await readJson(res);
      if (!res.ok) throw new Error(errText(data, "Could not connect to MT5."));
      setAccount(data.account);
      setForm({ login: "", password: "", server: "", broker: "" });
      setMsg({ type: "ok", text: "Connected. Importing your trade history — this updates automatically." });
      onSynced && onSynced();
      refreshEnt && refreshEnt();   // lift demo gates instantly
      setCelebrate(true);           // celebrate + auto-unlock nudge
      setTimeout(() => loadAccount(true), 4000);
      setTimeout(() => loadAccount(true), 9000);
    } catch (e) {
      setMsg({ type: "err", text: e.message });
      // Connect may have completed server-side even if the proxy timed out — refresh.
      loadAccount();
    }
    setBusy(false);
  };

  const syncNow = async () => {
    setBusy(true); setMsg(null);
    try {
      const res = await fetch(`${API}/mt5/sync`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({}),
      });
      const data = await readJson(res);
      if (!res.ok) throw new Error(errText(data, "Sync failed."));
      if (data.account) setAccount(data.account);
      setMsg({ type: "ok", text: "Syncing your latest trades…" });
      setTimeout(() => loadAccount(true), 4000);
      setTimeout(() => loadAccount(true), 9000);
    } catch (e) {
      setMsg({ type: "err", text: e.message });
      loadAccount();
    }
    setBusy(false);
  };

  const disconnect = async () => {
    setBusy(true); setMsg(null);
    try {
      await fetch(`${API}/mt5/disconnect`, { method: "POST", credentials: "include" });
      await loadAccount();
      setMsg(null);
    } catch {}
    setBusy(false);
  };

  const field = "w-full bg-[rgba(10,17,33,0.7)] border border-[color:var(--ws-border)] rounded-lg px-3 py-2.5 text-[14px] text-white outline-none focus:border-[color:var(--ws-blue)] transition-colors";

  if (loading) {
    return <div className="ws-panel p-6"><div className="flex items-center gap-2 text-[color:var(--ws-muted)] text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading MT5…</div></div>;
  }

  // Guests must sign in first (multi-user isolation)
  if (!user) {
    return (
      <div className="ws-panel p-6">
        <div className="flex items-center gap-2.5 mb-2">
          <div className="ws-icon-chip w-9 h-9"><Plug className="w-5 h-5" /></div>
          <h3 className="ws-heading text-[17px] font-semibold">Connect MT5</h3>
        </div>
        <p className="text-[13px] text-[color:var(--ws-muted)] mb-4">Sign in to securely connect your MetaTrader 5 account and auto-sync trades into your Journal.</p>
        <button onClick={login} className="ws-btn-primary px-5 py-2.5 rounded-lg text-[13px] font-semibold inline-flex items-center gap-2">
          <LogIn className="w-4 h-4" /> Sign in with Google
        </button>
      </div>
    );
  }

  const st = STATUS_MAP[account?.connection_status] || STATUS_MAP.disconnected;

  return (
    <div className="ws-panel p-6">
      {celebrate && <BrokerCelebration onEnter={() => { setCelebrate(false); navigate("/"); }} />}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className="ws-icon-chip w-9 h-9"><Plug className="w-5 h-5" /></div>
          <div>
            <h3 className="ws-heading text-[17px] font-semibold">{isConnected ? "MT5 Connected" : "Connect MT5"}</h3>
            {isConnected && (
              <span className={`text-[12px] inline-flex items-center gap-1.5 ${st.cls}`}>
                <st.Icon className={`w-3.5 h-3.5 ${account.connection_status === "syncing" ? "animate-spin" : ""}`} /> {st.label}
              </span>
            )}
          </div>
        </div>
        {isConnected && <CheckCircle2 className="w-6 h-6 ws-up" />}
      </div>

      {!isConnected ? (
        <>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-wide text-[color:var(--ws-muted)]">MT5 Account ID</label>
                <input value={form.login} onChange={(e) => setForm({ ...form, login: e.target.value })} placeholder="e.g. 5039281" className={`${field} mt-1.5`} data-testid="mt5-login-input" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide text-[color:var(--ws-muted)]">Investor / Read-Only Password</label>
                <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Investor password only" className={`${field} mt-1.5`} autoComplete="off" data-testid="mt5-password-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-wide text-[color:var(--ws-muted)]">Broker</label>
                <div className="relative mt-1.5">
                <select
                  value={form.broker}
                  onChange={(e) => {
                    const broker = e.target.value;
                    const servers = BROKERS[broker] || [];
                    // Changing broker resets the server (auto-select when only one option).
                    setForm({ ...form, broker, server: servers.length === 1 ? servers[0] : "" });
                  }}
                  className={`${field} appearance-none cursor-pointer pr-9`}
                  data-testid="mt5-broker-select"
                >
                  <option value="" disabled>Select broker</option>
                  {Object.keys(BROKERS).map((b) => (<option key={b} value={b}>{b}</option>))}
                </select>
                <ChevronDown className="w-4 h-4 text-[color:var(--ws-muted)] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide text-[color:var(--ws-muted)]">Server</label>
                <div className="relative mt-1.5">
                <select
                  value={form.server}
                  onChange={(e) => setForm({ ...form, server: e.target.value })}
                  disabled={!form.broker}
                  className={`${field} appearance-none cursor-pointer pr-9 disabled:opacity-50 disabled:cursor-not-allowed`}
                  data-testid="mt5-server-select"
                >
                  <option value="" disabled>{form.broker ? "Select server" : "Choose a broker first"}</option>
                  {(BROKERS[form.broker] || []).map((s) => (<option key={s} value={s}>{s}</option>))}
                </select>
                <ChevronDown className="w-4 h-4 text-[color:var(--ws-muted)] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-start gap-2 mt-4 mb-4 rounded-lg p-3" style={{ background: "rgba(52,211,153,0.06)", border: "1px solid rgba(52,211,153,0.2)" }}>
            <ShieldCheck className="w-4 h-4 ws-up shrink-0 mt-0.5" />
            <span className="text-[12px] text-[color:var(--ws-text)] opacity-90">Read-only connection. Wealth Secret Journal cannot place trades on your MT5 account. Use your <b>Investor</b> password only.</span>
          </div>

          {msg && (
            <div className="mb-3">
              <div className={`text-[13px] inline-flex items-center gap-1.5 ${msg.type === "ok" ? "ws-up" : "ws-down"}`}>
                {msg.type === "ok" ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />} {msg.text}
              </div>
              {msg.type === "err" && (
                <div className="mt-2 flex flex-wrap items-center gap-2 text-[12px] text-[color:var(--ws-muted)]" data-testid="mt5-connect-help">
                  <span>Still failing? Our team replies fast:</span>
                  <TelegramButton testid="mt5-tg-support-btn" />
                </div>
              )}
            </div>
          )}

          <button onClick={connect} disabled={busy} className="ws-btn-primary ws-cta ws-cta-elefin w-full sm:w-auto px-7 py-3 rounded-xl text-[14px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:animate-none" data-testid="mt5-connect-btn">
            <span className="ws-cta-shine" aria-hidden />
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LinkIcon className="w-4 h-4" />} Connect &amp; Sync <ArrowRight className="w-4 h-4" />
          </button>
        </>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
            {[
              ["Account ID", account.mt5_login],
              ["Server", account.mt5_server],
              ["Balance", account.balance != null ? `${account.balance} ${account.currency || ""}` : "—"],
              ["Equity", account.equity != null ? `${account.equity} ${account.currency || ""}` : "—"],
              ["Margin", account.margin != null ? `${account.margin} ${account.currency || ""}` : "—"],
              ["Free Margin", account.free_margin != null ? `${account.free_margin} ${account.currency || ""}` : "—"],
              ["Broker", account.company || "—"],
              ["Last Sync", account.last_sync_at ? fmtLocal(account.last_sync_at) : "—"],
            ].map(([k, v]) => (
              <div key={k} className="ws-price-cell p-3">
                <div className="text-[10px] uppercase tracking-wide text-[color:var(--ws-muted)]">{k}</div>
                <div className="ws-heading text-[15px] font-semibold text-white truncate">{v}</div>
              </div>
            ))}
          </div>

          {account.connection_status === "connection_error" && account.last_error && (
            <div className="mb-3" data-testid="mt5-connection-error">
              <div className="text-[13px] inline-flex items-start gap-1.5 ws-down">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{account.last_error.message || "Connection error."}{account.last_error.stage ? ` (${account.last_error.stage})` : ""}</span>
              </div>
              <div className="mt-2"><TelegramButton testid="mt5-error-tg-support-btn" /></div>
            </div>
          )}

          {msg && (
            <div className="mb-3">
              <div className={`text-[13px] inline-flex items-center gap-1.5 ${msg.type === "ok" ? "ws-up" : "ws-down"}`}>
                {msg.type === "ok" ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />} {msg.text}
              </div>
              {msg.type === "err" && (
                <div className="mt-2 flex flex-wrap items-center gap-2 text-[12px] text-[color:var(--ws-muted)]" data-testid="mt5-connect-help">
                  <span>Still failing? Our team replies fast:</span>
                  <TelegramButton testid="mt5-tg-support-btn" />
                </div>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2.5">
            <button onClick={syncNow} disabled={busy} className="ws-btn-primary px-4 py-2 rounded-lg text-[13px] font-semibold inline-flex items-center gap-2 disabled:opacity-60" data-testid="mt5-sync-now-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Sync Now
            </button>
            <button onClick={() => setAutoOn((v) => !v)} className="ws-btn-ghost px-3 py-2 rounded-lg text-[13px] font-medium inline-flex items-center gap-2" data-testid="mt5-autosync-toggle" title={autoOn ? "Pause auto-sync" : "Resume auto-sync"}>
              {autoOn ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />} {autoOn ? "Auto-sync On" : "Auto-sync Off"}
            </button>
            <button onClick={disconnect} disabled={busy} className="ws-btn-ghost px-4 py-2 rounded-lg text-[13px] font-medium inline-flex items-center gap-2">
              <Unplug className="w-4 h-4" /> Disconnect MT5
            </button>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2 text-[12px] text-[color:var(--ws-muted)]" data-testid="mt5-autosync-status">
            <span className={`inline-flex items-center gap-1.5 ${autoOn ? "ws-up" : ""}`}>
              <span className={`w-2 h-2 rounded-full ${autoOn ? "bg-[color:var(--ws-green)] ws-pulse-dot" : "bg-[color:var(--ws-muted)]"}`} />
              {autoOn ? "Live auto-sync on" : "Auto-sync paused"}
            </span>
            <span className="opacity-50">•</span>
            <span data-tick={tick}>Last synced {relativeTime(account.last_sync_at)}</span>
          </div>

          {setRangeDays && (
            <div className="mt-4 flex items-center gap-2" data-testid="mt5-sync-range">
              <span className="text-[11px] uppercase tracking-wide text-[color:var(--ws-muted)]">History range</span>
              <div className="inline-flex rounded-lg overflow-hidden border border-[color:var(--ws-border)]">
                {RANGES.map((r) => (
                  <button
                    key={r.label}
                    onClick={() => setRangeDays(r.days)}
                    data-testid={`mt5-range-${r.label.toLowerCase()}`}
                    className={`px-3 py-1.5 text-[12px] font-semibold transition-colors ${rangeDays === r.days ? "bg-[color:var(--ws-blue)] text-white" : "text-[color:var(--ws-muted)] hover:text-white"}`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ConnectMT5;
